/**
 * The stores counter.
 *
 * A technician arrives with a job number and a list. The storekeeper scans
 * what they are taking, scans the job, and hands it across. That is the whole
 * screen, and everything about it is shaped by the fact that the person using
 * it has both hands full and is not looking at the keyboard.
 *
 * ── the scanner is a keyboard ───────────────────────────────────────────
 *
 * Nearly every handheld stores scanner reads a symbol, types the characters
 * and presses Enter. So there is no driver here, no pairing and no permission
 * prompt: one text box that keeps the focus, and a submit on Enter. The same
 * box works for somebody with no scanner who types the number instead, which
 * is why this is not two screens.
 *
 * ── one box, not four ───────────────────────────────────────────────────
 *
 * The box does not ask what KIND of thing is being scanned. A catalogue
 * number, a bin's barcode, a manufacturer's part number and the work order on
 * the docket all go into the same place and `lookupCode` works out which it
 * is. A counter that made somebody choose the right field first would be
 * slower than the paper it replaced.
 *
 * ── it hands over what is there ─────────────────────────────────────────
 *
 * Five wanted and three on the shelf means three go across and the shortfall
 * is said out loud. See `issueBasket`.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import {
  heldAt,
  lookupCode,
  partsFor,
  type StockItem,
} from "../../domain/stores";
import {
  issueBasket,
  returnBasket,
  type CounterOutcome,
  type CounterResult,
  type Result,
} from "../../domain/storesEdit";
import { Button, Card, EmptyState, Field, Input, Segmented, Table } from "../../design/components";
import { Page } from "../../layout/AppShell";
import { x as ex } from "../executive/shared";
import { useStores } from "./useStores";
import { t, tn } from "../../i18n";
import s from "./Stores.module.css";

interface BasketRow {
  sku: string;
  name: string;
  qty: number;
  uom: string;
  location: string;
  available: number;
}

export function CounterPage() {
  const session = useSession();
  const entered = useStores();
  const register = entered.value;
  const now = new Date();

  const [mode, setMode] = useState<"issue" | "return">("issue");
  const [code, setCode] = useState("");
  const [job, setJob] = useState("");
  const [basket, setBasket] = useState<BasketRow[]>([]);
  const [said, setSaid] = useState<{ tone: "good" | "bad" | "warn"; text: string } | null>(null);
  const [done, setDone] = useState<CounterOutcome[] | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const box = useRef<HTMLInputElement>(null);

  /* the box keeps the focus: a scan that lands anywhere else is lost keystrokes */
  useEffect(() => {
    box.current?.focus();
  }, [basket.length, job, mode, done]);

  const where = (item: StockItem) =>
    [...item.bins].sort((a, b) => b.onHand - a.onHand)[0]?.location ?? register.locations[0]?.code ?? "";

  const add = (item: StockItem, by: string) => {
    const location = where(item);
    setBasket((rows) => {
      const found = rows.find((r) => r.sku === item.sku);
      if (found) return rows.map((r) => (r.sku === item.sku ? { ...r, qty: r.qty + 1 } : r));
      return [
        ...rows,
        {
          sku: item.sku,
          name: item.name,
          qty: 1,
          uom: item.uom,
          location,
          available: heldAt(item, location),
        },
      ];
    });
    setSaid({ tone: "good", text: t("{name} — found by {by}", { name: item.name, by: t(by) }) });
  };

  const take = (raw: string) => {
    const hit = lookupCode(raw, register);
    setRefused(null);
    setDone(null);
    switch (hit.kind) {
      case "item":
        add(hit.item, hit.by);
        break;
      case "workOrder":
        setJob(hit.number);
        setSaid({ tone: "good", text: t("Job {n}", { n: hit.number }) });
        break;
      case "location":
        setSaid({ tone: "warn", text: t("{name} — a store, not a part. Scan the part itself.", { name: hit.location.name }) });
        break;
      default:
        setSaid({
          tone: "bad",
          text: t("Nothing here is called {code}. Check the label, or add the part to the catalog first.", { code: hit.code }),
        });
    }
    setCode("");
  };

  /* what the job already has reserved, so the counter can prompt for it */
  const owed = useMemo(() => (job ? partsFor(job, register, now) : null), [job, register]);
  const unclaimed = (owed?.lines ?? []).filter(
    (l) => l.outstanding > 0 && !basket.some((b) => b.sku === l.reservation.sku),
  );

  const apply = async (r: Result | CounterResult) => {
    if (!r.ok) {
      setRefused(r.why);
      return;
    }
    setRefused(null);
    if ("outcomes" in r) setDone(r.outcomes);
    setSaid({ tone: "good", text: r.note ?? "" });
    const handed = basket;
    setBasket([]);
    const no = await entered.commit(r);
    if (no) {
      /* the platform refused: nothing was handed over, and the basket is back on the counter to try again */
      setRefused(no);
      setDone(null);
      setSaid(null);
      setBasket(handed);
    }
  };

  const handOver = () => {
    const lines = basket.map((b) => ({ sku: b.sku, qty: b.qty, location: b.location }));
    void apply(
      mode === "issue"
        ? issueBasket(register, { workOrder: job, lines }, { name: session.principal.name })
        : returnBasket(register, { workOrder: job, lines }, { name: session.principal.name }),
    );
  };

  const units = basket.reduce((n, b) => n + b.qty, 0);

  return (
    <Page
      eyebrow={t("{site} · Stores", { site: session.site.code })}
      title="The counter"
      actions={
        <div className={s.actions}>
          <Link to="/stores" className={ex.tag}>
            {t("Back to the desk")}
          </Link>
          <Link to="/stores/inventory" className={ex.tag}>
            {t("The whole inventory")}
          </Link>
        </div>
      }
    >
      <p className={s.lede}>
        {t(
          "Scan the parts and the job into this box, in any order. A handheld scanner works with no setup, or type the number.",
        )}
      </p>

      <div className={s.filters}>
        <Segmented
          ariaLabel={t("Taking parts out, or putting them back")}
          value={mode}
          onChange={(v) => {
            setMode(v);
            setBasket([]);
            setDone(null);
          }}
          options={[
            { value: "issue" as const, label: t("Taking parts out") },
            { value: "return" as const, label: t("Putting parts back") },
          ]}
        />
      </div>

      <Card
        title="Scan or type"
        sub="Catalog number, bin label, part number or work order"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (code.trim()) take(code);
          }}
        >
          <Input
            ref={box}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={t("scan here")}
            aria-label={t("Scan or type")}
            data-testid="counter-scan"
            autoComplete="off"
            className={s.scanBox}
          />
        </form>
        {said && (
          <p className={s[said.tone]} data-testid="counter-said">
            {said.text}
          </p>
        )}
        <div className={s.jobRow}>
          <Field label={t("The job")}>
            <Input
              value={job}
              onChange={(e) => setJob(e.target.value.toUpperCase())}
              placeholder="WO-000231"
              aria-label={t("The job")}
              data-testid="counter-job"
            />
          </Field>
          {owed && owed.lines.length > 0 && (
            <p className={s.meta} data-testid="counter-owed">
              {t("This job has {n} reserved.", { n: tn(owed.lines.length, "{n} line", "{n} lines") })}
            </p>
          )}
        </div>
        {mode === "issue" && unclaimed.length > 0 && (
          /* what the job asked for and has not collected: one press each */
          <div className={s.chips} data-testid="counter-reserved">
            {unclaimed.map((l) => (
              <button
                key={l.reservation.id}
                type="button"
                className={s.chip}
                onClick={() => {
                  const item = register.items.find((i) => i.sku === l.reservation.sku);
                  if (item) add(item, "reservation");
                }}
              >
                {t("add {qty} × {sku}", { qty: l.outstanding, sku: l.reservation.sku })}
              </button>
            ))}
          </div>
        )}
      </Card>

      {refused && (
        <p className={s.refused} role="alert" data-testid="counter-refused">
          {refused}
        </p>
      )}

      <Card
        title="The basket"
        sub={mode === "issue" ? "what is going across the counter" : "what is coming back"}
        actions={
          basket.length > 0 ? (
            <div className={s.actions}>
              <Button onClick={() => setBasket([])}>{t("Clear")}</Button>
              <Button variant="primary" data-testid="counter-hand-over" onClick={handOver}>
                {mode === "issue"
                  ? tn(units, "Hand over {n} unit", "Hand over {n} units")
                  : tn(units, "Put {n} unit back", "Put {n} units back")}
              </Button>
            </div>
          ) : undefined
        }
      >
        {basket.length === 0 ? (
          <EmptyState>{t("Nothing scanned yet.")}</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>{t("Line")}</th>
                <th>{t("Off which shelf")}</th>
                <th className={s.num}>{t("On that shelf")}</th>
                <th className={s.num}>{t("Quantity")}</th>
                <th />
              </tr>
            </thead>
            <tbody data-testid="counter-basket">
              {basket.map((b) => (
                <tr key={b.sku}>
                  <td>
                    <Link to={`/stores/${encodeURIComponent(b.sku)}`}>{b.name}</Link>
                    <div className={s.sku}>{b.sku}</div>
                  </td>
                  <td className={s.why}>
                    {register.locations.find((l) => l.code === b.location)?.name ?? b.location}
                  </td>
                  <td className={`${s.num} ${mode === "issue" && b.qty > b.available ? s.bad : ""}`}>
                    {`${b.available} ${b.uom}`}
                  </td>
                  <td className={s.num}>
                    <Input
                      type="number"
                      min={1}
                      value={String(b.qty)}
                      onChange={(e) =>
                        setBasket((rows) =>
                          rows.map((r) => (r.sku === b.sku ? { ...r, qty: Math.max(1, Number(e.target.value) || 1) } : r)),
                        )
                      }
                      aria-label={t("Quantity, {sku}", { sku: b.sku })}
                      data-testid={`counter-qty-${b.sku}`}
                      className={s.qtyBox}
                    />
                  </td>
                  <td>
                    <Button size="sm" onClick={() => setBasket((rows) => rows.filter((r) => r.sku !== b.sku))}>
                      {t("Remove")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {mode === "issue" && basket.some((b) => b.qty > b.available) && (
          <p className={s.note}>
            {t(
              "If the shelf holds less than asked, what's there is handed over and the shortfall is shown.",
            )}
          </p>
        )}
      </Card>

      {done && (
        <Card title="What went across" sub="kept until the next scan, so the docket can be written up">
          <Table>
            <thead>
              <tr>
                <th>{t("Line")}</th>
                <th className={s.num}>{t("Wanted")}</th>
                <th className={s.num}>{t("Given")}</th>
                <th>{t("Off which shelf")}</th>
                <th>{t("Note")}</th>
              </tr>
            </thead>
            <tbody data-testid="counter-done">
              {done.map((o) => (
                <tr key={o.sku}>
                  <td>
                    {o.name}
                    <div className={s.sku}>{o.sku}</div>
                  </td>
                  <td className={s.num}>{o.wanted}</td>
                  <td className={`${s.num} ${o.issued < o.wanted ? s.bad : s.good}`}>{o.issued}</td>
                  <td className={s.why}>
                    {register.locations.find((l) => l.code === o.location)?.name ?? o.location}
                  </td>
                  <td className={o.short ? s.bad : s.why}>{o.short ?? t("complete")}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      <p className={ex.saved}>{entered.savedTo}</p>
    </Page>
  );
}
